import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useFleetData } from "./FleetDataContext";
import Nav from "./Nav";
import { LazyImage } from "./FleetPage";
import { BLUEPRINT_SECTION_ORDER, blueprintSectionOf } from "./blueprintSections";

const ACCENT = "#fcd657";

/* ─── Star field background ──────────────────────────────────────────────── */
function StarField() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const stars = Array.from({ length: 200 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.5 + 0.3,
      a: Math.random(), speed: Math.random() * 0.003 + 0.001,
    }));
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.a += s.speed; if (s.a > 1) s.a = 0;
        const alpha = Math.sin(s.a * Math.PI) * 0.7 + 0.1;
        ctx.beginPath();
        ctx.arc(s.x * canvas.width, s.y * canvas.height, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100vh", pointerEvents: "none", zIndex: 0 }} />;
}

function SectionLabel({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
      <div style={{ width: 32, height: 2, background: ACCENT, flexShrink: 0 }} />
      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: ACCENT, textTransform: "uppercase" }}>{children}</span>
    </div>
  );
}

// Discord role id required for "Member" gating — mirrors App.js MEMBER_ROLE_ID.
const MEMBER_ROLE_ID = "1303561707196649512";

/* ─── Blueprint Detail (owners list) ─────────────────────────────────────── */
function BlueprintDetail({ blueprintId, onBack, allBlueprints, allProfiles }) {
  const { profile } = useAuth();
  // Member gating for the owner list. Honors member_status as the canonical
  // membership signal (the discord_roles snapshot can lag behind reality).
  const viewerIsMember = !!profile && (
    profile.member_status === 'member' ||
    profile.is_admin ||
    (Array.isArray(profile.discord_roles) && profile.discord_roles.includes(MEMBER_ROLE_ID))
  );
  const blueprint = useMemo(
    () => (allBlueprints || []).find(b => b.id === blueprintId) || null,
    [allBlueprints, blueprintId]
  );

  const owners = useMemo(() => {
    if (!blueprint) return [];
    const found = [];
    (allProfiles || []).forEach(p => {
      const bps = Array.isArray(p.blueprints) ? p.blueprints : [];
      const has = bps.some(entry => {
        const id = typeof entry === "string" ? entry : (entry && entry.blueprint_id);
        return id === blueprintId;
      });
      if (has) found.push(p);
    });
    return found;
  }, [allProfiles, blueprintId, blueprint]);

  if (!blueprint) {
    return (
      <div style={{ textAlign: "center", padding: "120px 0" }}>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: "rgba(255,255,255,0.2)", marginBottom: 20 }}>BLUEPRINT NOT FOUND</div>
        <button onClick={onBack} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", padding: "8px 20px", border: `1px solid ${ACCENT}`, color: ACCENT, background: "transparent", cursor: "pointer" }}>
          Back to Blueprints
        </button>
      </div>
    );
  }

  return (
    <div>
      <button onClick={onBack} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", padding: "8px 20px", border: `1px solid rgba(252,214,87,0.3)`, color: "rgba(232,234,246,0.5)", background: "transparent", cursor: "pointer", marginBottom: 32, transition: "all 0.2s" }}
        onMouseEnter={e => { e.currentTarget.style.color = ACCENT; e.currentTarget.style.borderColor = ACCENT; }}
        onMouseLeave={e => { e.currentTarget.style.color = "rgba(232,234,246,0.5)"; e.currentTarget.style.borderColor = "rgba(252,214,87,0.3)"; }}>
        &larr; Back to Blueprints
      </button>

      <div style={{ display: "flex", gap: 40, flexWrap: "wrap", marginBottom: 48 }}>
        <div style={{ width: "100%", maxWidth: 420, flexShrink: 0 }}>
          <div style={{ width: "100%", aspectRatio: "1/1", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(252,214,87,0.15)", overflow: "hidden", position: "relative" }}>
            {blueprint.image_url ? (
              <LazyImage src={blueprint.image_url} alt={blueprint.name} thumbnailWidth={840} style={{ width: "100%", height: "100%" }} />
            ) : (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 48, color: "rgba(252,214,87,0.18)" }}>
                ◇
              </div>
            )}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
            {blueprint.category && (
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: ACCENT, textTransform: "uppercase", padding: "3px 10px", border: `1px solid ${ACCENT}55`, background: `${ACCENT}10` }}>
                {blueprint.category}
              </span>
            )}
            {blueprint.family && (
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.55)", textTransform: "uppercase", padding: "3px 10px", border: "1px solid rgba(255,255,255,0.12)" }}>
                {blueprint.family}
              </span>
            )}
          </div>
          <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(22px, 3.5vw, 36px)", color: "#e8eaf6", marginBottom: 24 }}>
            {blueprint.name}
          </h1>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 24 }}>
            <div style={{ padding: "16px 18px", border: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)" }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: ACCENT, textTransform: "uppercase", marginBottom: 6 }}>Members With</div>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 28, color: "#e8eaf6" }}>{owners.length}</div>
            </div>
          </div>
        </div>
      </div>

      <SectionLabel>Owners</SectionLabel>
      <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 22, color: "#e8eaf6", marginBottom: 24 }}>
        {owners.length} {owners.length === 1 ? "Member" : "Members"}
      </h2>

      {owners.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0", fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>
          NO OWNERS YET
        </div>
      ) : !viewerIsMember ? (
        <div style={{ textAlign: "center", padding: "32px 24px", border: "1px dashed rgba(252,214,87,0.25)", background: "rgba(252,214,87,0.04)" }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: ACCENT, textTransform: "uppercase", marginBottom: 8 }}>
            Members Only
          </div>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.55)", lineHeight: 1.5 }}>
            Sign in with the Member role in our Discord to see who owns this blueprint.
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {owners.map(owner => (
            <Link key={owner.id} to={`/profile/${owner.id}`} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)", transition: "all 0.2s", textDecoration: "none", color: "inherit" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = `${ACCENT}44`; e.currentTarget.style.background = `${ACCENT}08`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", overflow: "hidden", border: "2px solid rgba(252,214,87,0.3)", flexShrink: 0 }}>
                {owner.discord_avatar ? (
                  <img src={owner.discord_avatar} alt={owner.discord_username} style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                ) : (
                  <div style={{ width: "100%", height: "100%", background: "#0d1120", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 16, color: ACCENT }}>
                    {owner.discord_username?.charAt(0)}
                  </div>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 16, color: "#e8eaf6" }}>{owner.discord_username}</div>
                {owner.org_role && (
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>{owner.org_role}</div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Blueprint Grid (org view) ──────────────────────────────────────────── */
function BlueprintGrid({ blueprints, profiles, blueprintOwnerCache, onSelect }) {
  const [search, setSearch] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterFamily, setFilterFamily] = useState("");
  const [showOwnedOnly, setShowOwnedOnly] = useState(true);

  const ownerData = useMemo(() => {
    if (blueprintOwnerCache) return blueprintOwnerCache;
    const data = {};
    blueprints.forEach(b => { data[b.id] = { owners: 0 }; });
    (profiles || []).forEach(p => {
      const userBps = Array.isArray(p.blueprints) ? p.blueprints : [];
      const seen = new Set();
      userBps.forEach(entry => {
        const id = typeof entry === "string" ? entry : (entry && entry.blueprint_id);
        if (id && data[id] && !seen.has(id)) {
          seen.add(id);
          data[id].owners += 1;
        }
      });
    });
    return data;
  }, [blueprints, profiles, blueprintOwnerCache]);

  // Distinct groups, categories, and families from the catalog (or only those
  // with owners). Filters cascade — group narrows the categories shown, and
  // group+category narrows the families.
  const visiblePool = useMemo(() => (
    showOwnedOnly
      ? blueprints.filter(b => (ownerData[b.id]?.owners || 0) > 0)
      : blueprints
  ), [blueprints, ownerData, showOwnedOnly]);

  const groups = useMemo(
    () => [...new Set(visiblePool.map(b => b.item_group).filter(Boolean))].sort(),
    [visiblePool]
  );

  const categories = useMemo(() => {
    const pool = filterGroup ? visiblePool.filter(b => b.item_group === filterGroup) : visiblePool;
    return [...new Set(pool.map(b => b.category).filter(Boolean))].sort();
  }, [visiblePool, filterGroup]);

  const families = useMemo(() => {
    let pool = filterGroup ? visiblePool.filter(b => b.item_group === filterGroup) : visiblePool;
    if (filterCategory) pool = pool.filter(b => b.category === filterCategory);
    return [...new Set(pool.map(b => b.family).filter(Boolean))].sort();
  }, [visiblePool, filterGroup, filterCategory]);

  // If a narrower filter goes out of scope (because group changed), drop it.
  useEffect(() => {
    if (filterCategory && !categories.includes(filterCategory)) setFilterCategory("");
  }, [categories, filterCategory]);
  useEffect(() => {
    if (filterFamily && !families.includes(filterFamily)) setFilterFamily("");
  }, [families, filterFamily]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return blueprints.filter(b => {
      const data = ownerData[b.id] || { owners: 0, totalCount: 0 };
      if (showOwnedOnly && data.owners === 0) return false;
      if (filterGroup && b.item_group !== filterGroup) return false;
      if (filterCategory && b.category !== filterCategory) return false;
      if (filterFamily && b.family !== filterFamily) return false;
      if (!q) return true;
      const hay = `${b.name} ${b.item_group || ""} ${b.category || ""} ${b.family || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [blueprints, ownerData, showOwnedOnly, filterGroup, filterCategory, filterFamily, search]);

  // Sort: owners desc, then name asc
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const ao = ownerData[a.id]?.owners || 0;
      const bo = ownerData[b.id]?.owners || 0;
      if (bo !== ao) return bo - ao;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [filtered, ownerData]);

  const distinctOwned = Object.values(ownerData).filter(d => d.owners > 0).length;

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <SectionLabel>Org Blueprint Tracker</SectionLabel>
        <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(28px, 4vw, 48px)", color: "#e8eaf6", marginBottom: 8 }}>
          Blueprints
        </h1>
        <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 17, color: "rgba(232,234,246,0.45)" }}>
          {distinctOwned} distinct blueprints owned across {(profiles || []).length} org members.
        </p>
      </div>

      {/* Search & filters — Group → Category → Family (each narrows the next) */}
      <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <input type="text" placeholder="Search blueprints…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200, padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(252,214,87,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2 }} />
        <select value={filterGroup} onChange={e => { setFilterGroup(e.target.value); setFilterCategory(""); setFilterFamily(""); }}
          style={{ padding: "10px 14px", background: "#0a0610", border: "1px solid rgba(252,214,87,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, minWidth: 160 }}>
          <option value="">All Groups</option>
          {groups.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setFilterFamily(""); }}
          style={{ padding: "10px 14px", background: "#0a0610", border: "1px solid rgba(252,214,87,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, minWidth: 160 }}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterFamily} onChange={e => setFilterFamily(e.target.value)}
          style={{ padding: "10px 14px", background: "#0a0610", border: "1px solid rgba(252,214,87,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, minWidth: 160 }}>
          <option value="">All Families</option>
          {families.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 10 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1, color: "rgba(232,234,246,0.6)", textTransform: "uppercase", cursor: "pointer" }}>
          <input type="checkbox" checked={showOwnedOnly} onChange={e => setShowOwnedOnly(e.target.checked)} />
          Owned only
        </label>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 16, marginBottom: 32, flexWrap: "wrap" }}>
        {[
          { label: "Showing", value: filtered.length },
          { label: "Catalog Size", value: blueprints.length },
          { label: "Distinct Owned", value: distinctOwned },
        ].map(stat => (
          <div key={stat.label} style={{ padding: "16px 24px", border: "1px solid rgba(252,214,87,0.15)", background: `${ACCENT}06`, flex: "1 1 120px", minWidth: 120 }}>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 28, color: ACCENT, lineHeight: 1, marginBottom: 4 }}>{stat.value}</div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.35)", textTransform: "uppercase" }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Grouped grid — Ammo / Armor / Personal Weapons / Ship Components / …
          matches the in-game inventory UI and the per-member profile page. */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 0", fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>
          {search || filterGroup || filterCategory || filterFamily
            ? "NO BLUEPRINTS MATCH YOUR FILTERS"
            : showOwnedOnly
              ? "NO OWNED BLUEPRINTS — UNCHECK 'OWNED ONLY' TO BROWSE THE CATALOG"
              : "NO BLUEPRINTS IN THE CATALOG"}
        </div>
      ) : (() => {
        // Bucket sorted blueprints into section → category → entries[].
        // `sorted` already applies the owner-count desc + name asc ordering,
        // so iteration here preserves that within each subcategory.
        const sections = {};
        sorted.forEach(bp => {
          const secLabel = blueprintSectionOf(bp);
          const catLabel = bp.category || 'Uncategorized';
          if (!sections[secLabel]) sections[secLabel] = {};
          if (!sections[secLabel][catLabel]) sections[secLabel][catLabel] = [];
          sections[secLabel][catLabel].push(bp);
        });
        const orderedLabels = BLUEPRINT_SECTION_ORDER.filter(l => sections[l]);

        const renderCard = (bp) => {
          const data = ownerData[bp.id] || { owners: 0 };
          return (
            <div key={bp.id} onClick={() => onSelect(bp.id)} style={{ border: `1px solid rgba(252,214,87,0.15)`, background: "rgba(255,255,255,0.02)", cursor: "pointer", transition: "all 0.25s", overflow: "hidden", clipPath: "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)", position: "relative" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = `${ACCENT}55`; e.currentTarget.style.background = `${ACCENT}08`; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = `rgba(252,214,87,0.15)`; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; e.currentTarget.style.transform = "translateY(0)"; }}>
              <div style={{ position: "relative", width: "100%", aspectRatio: "1/1", background: "rgba(0,0,0,0.3)", overflow: "hidden" }}>
                {bp.image_url ? (
                  <LazyImage src={bp.image_url} alt={bp.name} thumbnailWidth={400} style={{ width: "100%", height: "100%" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 36, color: "rgba(252,214,87,0.12)" }}>
                    ◇
                  </div>
                )}
                {data.owners > 0 && (
                  <div className="bp-badge" style={{ position: "absolute", top: 6, right: 6, background: "rgba(5,7,15,0.85)", backdropFilter: "blur(8px)", padding: "4px 10px", border: `1px solid ${ACCENT}44` }}>
                    <span className="bp-badge-num" style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, color: ACCENT }}>{data.owners}</span>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", marginLeft: 4 }}>OWNED BY</span>
                  </div>
                )}
                {bp.category && (
                  <div className="bp-badge" style={{ position: "absolute", top: 6, left: 6, background: "rgba(5,7,15,0.85)", backdropFilter: "blur(8px)", padding: "3px 8px", border: "1px solid rgba(255,255,255,0.12)" }}>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, color: "rgba(232,234,246,0.7)", textTransform: "uppercase" }}>{bp.category}</span>
                  </div>
                )}
              </div>
              <div className="bp-card-content" style={{ padding: "12px 14px" }}>
                <div className="bp-name" style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, color: "#e8eaf6", marginBottom: 4, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{bp.name}</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                  <div className="bp-meta" style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "rgba(232,234,246,0.4)", textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{bp.family || ""}</div>
                  <div className="bp-meta" style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: `${ACCENT}99`, flexShrink: 0 }}>
                    {data.owners} {data.owners === 1 ? "owner" : "owners"}
                  </div>
                </div>
              </div>
            </div>
          );
        };

        return (
          <div>
            {orderedLabels.map(secLabel => {
              const catMap = sections[secLabel];
              const sectionTotal = Object.values(catMap).reduce((acc, list) => acc + list.length, 0);
              return (
                <div key={secLabel} style={{ marginBottom: 32 }}>
                  {/* Section header — matches the AMMO/ARMOR bar style on the profile page */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "rgba(252,214,87,0.08)", borderLeft: "3px solid #fcd657", borderTop: "1px solid rgba(252,214,87,0.18)", borderRight: "1px solid rgba(252,214,87,0.18)", borderBottom: "1px solid rgba(252,214,87,0.18)", marginBottom: 14 }}>
                    <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: 3, color: "#fcd657", textTransform: "uppercase" }}>
                      {secLabel}
                    </span>
                    <div style={{ flex: 1, height: 1, background: "rgba(252,214,87,0.18)" }} />
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.5)", letterSpacing: 1 }}>
                      {sectionTotal}
                    </span>
                  </div>

                  {/* Subcategory blocks within the section, alphabetical */}
                  {Object.entries(catMap)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([catLabel, list]) => (
                      <div key={catLabel} style={{ marginBottom: 22 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, paddingLeft: 4 }}>
                          <span style={{ color: "#fcd657", fontSize: 10, lineHeight: 1 }}>◆</span>
                          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: "rgba(232,234,246,0.7)", textTransform: "uppercase" }}>
                            {catLabel}
                          </span>
                          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.3)", letterSpacing: 1 }}>
                            {list.length}
                          </span>
                        </div>
                        <div className="bp-grid">
                          {list.map(renderCard)}
                        </div>
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}

/* ─── Main page ──────────────────────────────────────────────────────────── */
export default function BlueprintsPage() {
  const { blueprintId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { blueprints, profiles, blueprintOwnerCache, loaded, loading } = useFleetData();
  const [selected, setSelected] = useState(blueprintId || null);

  useEffect(() => { setSelected(blueprintId || null); }, [blueprintId]);

  // Preserve grid scroll across a card-click → detail → Back round trip.
  const SCROLL_KEY = "blueprintsGridScrollY";
  const handleSelect = useCallback((id) => {
    try { sessionStorage.setItem(SCROLL_KEY, String(window.scrollY)); } catch (e) {}
    setSelected(id);
    navigate(`/resources/blueprints/${id}`);
  }, [navigate]);

  // Use in-app history when available so a back from a blueprint detail
  // returns to the referring page; deep-linked visits fall back to the grid.
  const handleBack = useCallback(() => {
    setSelected(null);
    const idx = window.history.state?.idx;
    if (typeof idx === "number" && idx > 0) navigate(-1);
    else navigate("/resources/blueprints");
  }, [navigate]);

  // Restore the saved scroll when returning to the grid view. rAF waits for
  // render, the 200ms timeout catches LazyImage-induced layout shifts.
  useEffect(() => {
    if (selected != null) return;
    let saved;
    try { saved = sessionStorage.getItem(SCROLL_KEY); } catch (e) { return; }
    if (saved == null) return;
    const y = parseInt(saved, 10);
    if (!Number.isFinite(y)) return;
    try { sessionStorage.removeItem(SCROLL_KEY); } catch (e) {}
    const raf = requestAnimationFrame(() => window.scrollTo(0, y));
    const t = setTimeout(() => window.scrollTo(0, y), 200);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [selected]);

  if (!loaded || loading) {
    return (
      <div style={{ background: "#05070f", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: `${ACCENT}99` }}>
        LOADING…
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Rajdhani', 'Orbitron', sans-serif", background: "#05070f", color: "#e8eaf6", minHeight: "100vh", overflowX: "hidden" }}>
      <StarField />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&family=Orbitron:wght@400;600;700;900&family=Space+Mono&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: #fcd657; color: #05070f; }
        a { text-decoration: none; color: inherit; }
        .nav-link { font-family: 'Orbitron', sans-serif; font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(232,234,246,0.55); transition: color 0.2s; padding: 4px 0; position: relative; }
        .nav-link::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 1px; background: #fcd657; transition: width 0.3s ease; }
        .nav-link:hover { color: #fcd657; }
        .nav-link:hover::after { width: 100%; }
        .scanline { position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 9999; background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px); }
        input, select, textarea { color-scheme: dark; }
        input:focus, select:focus, textarea:focus { outline: none; border-color: ${ACCENT} !important; }
        /* Blueprint card grid — wider min on desktop, shrinks to fit ~3 cards across on mobile portrait. */
        .bp-grid { display: grid; gap: 16px; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); }
        @media (max-width: 700px) {
          .bp-grid { gap: 8px; grid-template-columns: repeat(auto-fill, minmax(108px, 1fr)); }
          .bp-card-content { padding: 8px 9px !important; }
          .bp-name { font-size: 11px !important; line-height: 1.25 !important; }
          .bp-meta { font-size: 8px !important; }
          .bp-badge { font-size: 7px !important; padding: 2px 5px !important; }
          .bp-badge-num { font-size: 9px !important; }
        }
      `}</style>
      <div className="scanline" />
      <Nav accentColor={ACCENT} />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto", padding: "112px clamp(16px, 4vw, 40px) 48px" }}>
        {/* Resources breadcrumb */}
        <div style={{ marginBottom: 18 }}>
          <Link to="/resources" style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.45)", textTransform: "uppercase" }}>
            &larr; Resources
          </Link>
        </div>

        {selected ? (
          <BlueprintDetail blueprintId={selected} onBack={handleBack} allBlueprints={blueprints} allProfiles={profiles} />
        ) : (
          <BlueprintGrid blueprints={blueprints} profiles={profiles} blueprintOwnerCache={blueprintOwnerCache} onSelect={handleSelect} />
        )}
      </div>

      <footer style={{ position: "relative", zIndex: 1, borderTop: "1px solid rgba(255,255,255,0.07)", padding: "32px 40px", background: "rgba(0,0,0,0.4)", marginTop: 60 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, color: "rgba(232,234,246,0.18)", lineHeight: 1.8 }}>
          &copy; 2026 Starkeeper Industries. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
